import type { CSSProperties, ReactNode } from "react";
import { Avatar, AssignmentChip, vars } from "@pack-it-up/ui-web";
import {
  nextPackingState,
  packingStateOf,
  relevantParticipants,
  type Completable,
  type CompletionField,
  type PackingState,
  type ParticipantContext,
} from "@pack-it-up/core";
import type { ParticipantId } from "@pack-it-up/shared";
import { CheckBadgeIcon } from "../../app/icons.js";

// Trailing assignment visual for an item/to-do row (gap-analysis §4, T-E), replacing the
// old `AssignmentBadge`. Reused by both PackingList and TodoList rows since packing items
// and to-dos share the same completion model.
// - shared → amber "Shared" tag (kit `.tag-shared`); no per-person detail needed since the
//   checkbox itself already carries a tooltip with who checked it.
// - everyone/specific → one avatar chip per relevant participant (everyone → all trip
//   participants, specific → allocatees). User-participant chips are DISPLAY ONLY —
//   unless the trip's "pack for others" setting is on (T23 §2), which makes them
//   clickable like managed chips. Managed profiles have no login and can't self-check,
//   so their chip is always clickable by any editor to toggle that profile's completion
//   directly (revised confirmation 1, gap-analysis "Resolved confirmations" §1) — same
//   toggle path `CompletionControl` already uses.
//
// The chip reuses the shared `Avatar` component ("xs" size) so a person's initials/color
// exactly match their avatar in the travellers pill (issue: avatar inconsistency). Instead
// of dimming the whole avatar when unchecked, it stays at full color always and gets a
// small green check badge in the bottom-right corner once that person has completed it —
// the badge carries the state, not the avatar's own lightness.
const wrapperBase: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  border: "none",
  padding: 0,
  background: "transparent",
  fontFamily: "inherit",
  flexShrink: 0,
};

const badgeStyle: CSSProperties = {
  position: "absolute",
  right: "-2px",
  bottom: "-2px",
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  background: vars.color.success,
  border: `1.5px solid ${vars.color.surface}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
};

// T27's "staged" (pulled out, not yet packed) — a plain amber dot, same spot/size as the
// green check badge. No icon inside: the dot itself is the signal, the checkmark is
// reserved for "packed".
const stagedBadgeStyle: CSSProperties = { ...badgeStyle, background: vars.color.accent };

// Label for a participant's tri-state, matching the checkbox's own wording ("staged" is a
// fixed term; "packed"/"not packed" follow the caller's `pastTense`).
const labelFor = (state: PackingState, pastTense: string): string =>
  state === "packed" ? pastTense : state === "staged" ? "staged" : `not ${pastTense}`;

export const AssignmentChips = ({
  item,
  list,
  pastTense = "packed",
  canEdit = false,
  field = "completions",
  stagingEnabled = false,
  onToggle,
}: {
  item: Completable;
  // Trip or template (issue #188) — see CompletionControl.
  list: ParticipantContext;
  // Wording for the tooltip/aria-label: items are "packed", to-dos are "done".
  pastTense?: string;
  // Which per-participant map the chips reflect (issue #188): a trip shows who has
  // packed the row, a template who it is staged for.
  field?: CompletionField;
  // T27: when true (trip packing items only — never passed for to-dos or templates),
  // chips reflect the 3-way unstaged/staged/packed cycle instead of the plain boolean, via
  // the same `packingStateOf` the checkbox uses.
  stagingEnabled?: boolean;
  // When true and `onToggle` is provided, managed-profile chips render as clickable
  // buttons. User-participant chips stay display-only spans unless the trip's
  // `allowProxyCompletion` setting is on.
  canEdit?: boolean;
  onToggle?: (target: ParticipantId, targetIsUser: boolean) => void;
}): ReactNode => {
  if (item.assignment.kind === "shared") {
    return <AssignmentChip variant="shared">Shared</AssignmentChip>;
  }

  const participants = relevantParticipants(item, list);
  return (
    <span style={{ display: "inline-flex", gap: "4px", flexShrink: 0 }}>
      {participants.map((pid) => {
        const member = list.members[pid];
        if (member === undefined) return null;
        let state: PackingState;
        if (stagingEnabled) {
          state = packingStateOf(item, pid, true);
        } else {
          const map = field === "completions" ? item.completions : item.staging;
          state = (map?.byParticipant[pid]?.done ?? false) ? "packed" : "unstaged";
        }
        const name = member.profile.displayName;
        const title = `${name} — ${labelFor(state, pastTense)}`;
        const badge =
          state === "packed" ? (
            <span style={badgeStyle} aria-hidden>
              {CheckBadgeIcon()}
            </span>
          ) : state === "staged" ? (
            <span style={stagedBadgeStyle} aria-hidden />
          ) : null;
        const clickable =
          member.kind === "managed" || list.settings.allowProxyCompletion;
        if (clickable && canEdit && onToggle !== undefined) {
          return (
            <button
              key={pid}
              type="button"
              title={title}
              aria-label={`Mark ${name} as ${labelFor(nextPackingState(state, stagingEnabled), pastTense)}`}
              style={{ ...wrapperBase, cursor: "pointer" }}
              onClick={() => onToggle(pid, member.kind === "user")}
            >
              <Avatar name={name} size="xs" decorative />
              {badge}
            </button>
          );
        }
        return (
          <span key={pid} title={title} style={wrapperBase}>
            <Avatar name={name} size="xs" decorative />
            {badge}
          </span>
        );
      })}
    </span>
  );
};
